import { UIElement } from '../ui-element';
import { Labels } from '../../global/labels';
import { Attribute } from '../../decorators/attribute.decorator';
import type { ElementConfig, Props } from '../ui-element.type';
import { rebuildChildren } from '../../global/mutations/index';
import {
    createElement,
    updateClassList,
    updateElement,
} from '../../global/render-api';
import { Component } from '../../decorators/component.decorator';
import { Child } from '../../decorators/child.decorator';
import type { TranslationLabels } from '../../types/labels.type';
import type { UIBreadCrumbsItem } from './menu.type';
import styles from './ui-breadcrumbs.css';

/**
 * Configuration object for a breadcrumb item.
 * @interface
 * @property {string} [href] - The URL the breadcrumb item links to.
 * @property {string} [label] - The display text of the breadcrumb item.
 * @deprecated - use UIBreadCrumbsItem instead
 */
export interface IBreadcrumbsItem extends Props {
    href?: string;
    label?: string;
}

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIBreadCrumbs
 * @element ui-breadcrumbs
 * @classdesc Represents a class for <code>ui-breadcrumbs</code> element
 * @property {boolean} [business] {@attr business} Whether the breadcrumbs are for business.
 * @property {number[]} [ignoreLevels] {@attr ignore-levels} Levels to ignore.
 * @example
 * <ui-breadcrumbs></ui-breadcrumbs>
 */
@Component({
    tag: 'ui-breadcrumbs',
    styles,
})
export class UIBreadCrumbs extends UIElement {
    @Attribute(Boolean) business!: boolean;
    @Attribute(String) hrefStart!: string;
    @Attribute(String) labelStart!: string;
    @Child('.breadcrumbs') breadcrumbsContainer!: HTMLDivElement;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-breadcrumbs', {
            title: 'Breadcrumbs navigation',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): Array<string> {
        return ['business'];
    }

    /**
     * @returns {number[]}
     */
    get ignoreLevels(): Array<number> {
        const value = this.getAttribute('ignore-levels');
        if (!value) {
            return [];
        }
        return value.split(',').map(Number);
    }

    /**
     * @param {IBreadcrumbsItem[]} items
     * @returns {ElementConfig[]}
     */
    buildItems(items: Array<UIBreadCrumbsItem>): Array<ElementConfig> {
        return items.map((config, i) => {
            const anchorElement = config.href
                ? createElement({
                      tagName: 'a',
                      attributes: {
                          href: config.href || null,
                      },
                      children: [config.label ?? null],
                  })
                : (config.label ?? null);
            const iconElement =
                i < items.length - 1
                    ? createElement({
                          tagName: 'ui-icon',
                          attributes: {
                              glyph: 'redesign-chevron-right',
                          },
                      })
                    : '';

            return {
                tagName: 'li',
                classList: {
                    'breadcrumb': true,
                    '-current': i === items.length - 1,
                },
                attributes: {
                    'aria-current': i === items.length - 1 ? 'page' : null,
                },
                children: [anchorElement, iconElement],
            };
        });
    }

    /**
     * @param {IBreadcrumbsItem[]} [items]
     */
    setItems(items: Array<UIBreadCrumbsItem>): void {
        const ignoredLevels = this.ignoreLevels;
        rebuildChildren(
            this.breadcrumbsContainer,
            this.buildItems(
                items.filter((_, i) => !ignoredLevels.includes(i + 1))
            )
        );
    }

    /**
     * Fires callback when environment is updated
     * @private
     */
    onChangeEnvironment(): void {
        updateClassList(this, {
            '-business': this.business,
            '-private': !this.business,
        });
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name: string, oldValue: string, newValue: string): void {
        switch (name) {
            case 'business':
                this.onChangeEnvironment();
                break;
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        updateElement(this, {
            classList: {
                '-business': this.business,
                '-private': !this.business,
            },
            children: [
                {
                    tagName: 'nav',
                    classList: {
                        'breadcrumbs-wrapper': true,
                    },
                    attributes: {
                        'aria-label': UIBreadCrumbs.labels.title,
                    },
                    children: [
                        {
                            tagName: 'ol',
                            classList: {
                                breadcrumbs: true,
                            },
                        },
                    ],
                },
            ],
        });
    }
}
